import React, { createContext, useContext, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { record, takeFullSnapshot } from 'rrweb';
import {
  connectMessaging, onMessagingEvent,
  cobrowseStart, cobrowseJoin, cobrowseLeave, cobrowseEnd,
  cobrowseInvite, cobrowseCursor, cobrowseNav,
  cobrowseRR, cobrowseRRRequest, cobrowseResume, cobrowseSetController, cobrowseInput,
} from '@/lib/messagingSocket';

const CollabContext = createContext(null);
export const useCollab = () => useContext(CollabContext);

// Deterministic bright color per participant so cursors are distinguishable.
const CURSOR_COLORS = ['#6366f1', '#06b6d4', '#f59e0b', '#ef4444', '#10b981', '#a855f7', '#ec4899', '#3b82f6'];
export function colorForUser(userId = '') {
  let h = 0;
  for (let i = 0; i < userId.length; i++) h = (h * 31 + userId.charCodeAt(i)) >>> 0;
  return CURSOR_COLORS[h % CURSOR_COLORS.length];
}
export function initialsFor(name = '') {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  return (parts[0][0] + (parts[1]?.[0] || '')).toUpperCase();
}

const COLLAB_SCROLL = '[data-collab-scroll]';

// Actions that must stay HOST-only even when a guest holds control.
const DESTRUCTIVE_RE = /\b(delete|remove|publish|promote|destroy|drop|revoke|deactivate|disable|danger|permission|security)\b/i;
function isProtectedTarget(el) {
  let n = el, depth = 0;
  while (n && depth < 4) {
    if (n.getAttribute?.('data-collab-protected') != null) return true;
    const s = `${n.getAttribute?.('data-testid') || ''} ${n.getAttribute?.('aria-label') || ''} ${n.getAttribute?.('title') || ''} ${(n.textContent || '').slice(0, 40)}`;
    if (DESTRUCTIVE_RE.test(s)) return true;
    n = n.parentElement; depth++;
  }
  return false;
}
// Executed on the HOST: replay a controlling guest's input on the real DOM.
function performRemoteInput(input) {
  if (!input) return;
  try {
    if (input.kind === 'click') {
      const el = document.elementFromPoint(input.x, input.y);
      if (!el) return;
      if (isProtectedTarget(el)) { toast.error('That action is restricted to the host'); return; }
      if (/^(input|textarea|select)$/i.test(el.tagName) || el.isContentEditable) { try { el.focus(); } catch { /* ignore */ } }
      const opts = { bubbles: true, cancelable: true, clientX: input.x, clientY: input.y, view: window };
      el.dispatchEvent(new MouseEvent('mousedown', opts));
      el.dispatchEvent(new MouseEvent('mouseup', opts));
      el.dispatchEvent(new MouseEvent('click', opts));
    } else if (input.kind === 'value') {
      const el = document.activeElement;
      if (!el || !('value' in el) || isProtectedTarget(el)) return;
      const proto = el instanceof HTMLTextAreaElement ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype;
      const setter = Object.getOwnPropertyDescriptor(proto, 'value')?.set;
      setter ? setter.call(el, input.value) : (el.value = input.value);
      el.dispatchEvent(new Event('input', { bubbles: true }));
    } else if (input.kind === 'key') {
      const el = document.activeElement || document.body;
      el.dispatchEvent(new KeyboardEvent('keydown', { key: input.key, bubbles: true }));
      el.dispatchEvent(new KeyboardEvent('keyup', { key: input.key, bubbles: true }));
    }
  } catch { /* best-effort */ }
}

export function CollabProvider({ children }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [session, setSession] = useState(null);        // {sessionId, hostId, controllerId, members[], isHost}
  const [cursors, setCursors] = useState({});          // uid -> {x,y,name,color,ts}
  const [invite, setInvite] = useState(null);          // incoming {sessionId, hostId, hostName}

  const sessionRef = useRef(null);
  const applyingRemoteNav = useRef(false);
  const applyRemoteNavRef = useRef(null);
  const recordStopRef = useRef(null);
  const mirrorListenersRef = useRef(new Set());
  sessionRef.current = session;

  const isController = session && user && session.controllerId === user.user_id;
  const isHost = !!session?.isHost;

  // Short two-tone chime to alert an invited user (Web Audio, no asset needed).
  const playInviteSound = useCallback(() => {
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      const ctx = new AC();
      const now = ctx.currentTime;
      const beep = (freq, start, dur) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = freq;
        gain.gain.setValueAtTime(0.0001, now + start);
        gain.gain.exponentialRampToValueAtTime(0.25, now + start + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + start + dur);
        osc.connect(gain); gain.connect(ctx.destination);
        osc.start(now + start); osc.stop(now + start + dur);
      };
      beep(660, 0, 0.18);
      beep(880, 0.16, 0.28);
      setTimeout(() => { try { ctx.close(); } catch { /* ignore */ } }, 800);
    } catch { /* autoplay blocked or unsupported */ }
  }, []);

  // ── Receive events ───────────────────────────────────────────────
  useEffect(() => {
    connectMessaging();
    const off = onMessagingEvent((evt) => {
      if (!evt || !evt.type) return;
      const cur = sessionRef.current;
      switch (evt.type) {
        case 'cobrowse_state': {
          if (!cur || cur.sessionId !== evt.session_id) return;
          setSession((s) => s && s.sessionId === evt.session_id
            ? { ...s, hostId: evt.host_id, controllerId: evt.controller_id, members: evt.members || [] }
            : s);
          break;
        }
        case 'cobrowse_cursor': {
          if (!cur || cur.sessionId !== evt.session_id) return;
          setCursors((c) => ({ ...c, [evt.user_id]: { x: evt.x, y: evt.y, name: evt.name, color: colorForUser(evt.user_id), ts: Date.now() } }));
          break;
        }
        case 'cobrowse_nav': {
          if (!cur || cur.sessionId !== evt.session_id) return;
          applyRemoteNavRef.current?.(evt.context);
          break;
        }
        case 'cobrowse_rr': {
          // guest: feed an incoming rrweb event to the live mirror
          if (!cur || cur.sessionId !== evt.session_id) return;
          mirrorListenersRef.current.forEach((cb) => { try { cb(evt.event); } catch { /* ignore */ } });
          break;
        }
        case 'cobrowse_rr_request': {
          // host: a guest needs a fresh full DOM snapshot — emit one
          if (recordStopRef.current) { try { takeFullSnapshot(true); } catch { /* ignore */ } }
          break;
        }
        case 'cobrowse_input': {
          // host: execute a controlling guest's input on the real DOM (guarded)
          if (!cur || cur.sessionId !== evt.session_id) return;
          performRemoteInput(evt.input);
          break;
        }
        case 'cobrowse_invite': {
          // ignore if it's my own session or I'm already in one
          if (sessionRef.current) return;
          setInvite({ sessionId: evt.session_id, hostId: evt.host_id, hostName: evt.host_name });
          playInviteSound();
          break;
        }
        case 'cobrowse_ended': {
          if (cur && cur.sessionId === evt.session_id) {
            setSession(null); setCursors({});
            try { sessionStorage.removeItem('collabSession'); } catch { /* ignore */ }
            toast.message('Live session ended', { description: 'The host has ended the session.' });
          }
          break;
        }
        case 'cobrowse_unavailable': {
          toast.error('That live session is no longer available.');
          setInvite((i) => (i && i.sessionId === evt.session_id ? null : i));
          break;
        }
        default: break;
      }
    });
    return off;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Drop stale cursors (participant idle >5s).
  useEffect(() => {
    if (!session) return;
    const id = setInterval(() => {
      setCursors((c) => {
        const now = Date.now(); let changed = false; const next = {};
        for (const [k, v] of Object.entries(c)) { if (now - v.ts < 5000) next[k] = v; else changed = true; }
        return changed ? next : c;
      });
    }, 2000);
    return () => clearInterval(id);
  }, [session]);

  // ── Follow-the-host: apply an incoming nav context (guests) ───────
  const applyRemoteNav = useCallback((ctx) => {
    if (!ctx) return;
    applyingRemoteNav.current = true;
    const target = ctx.route;
    if (target && (location.pathname + location.search) !== target) {
      navigate(target);
    }
    // apply scroll after the route settles
    setTimeout(() => {
      const el = document.querySelector(COLLAB_SCROLL);
      if (el && typeof ctx.scrollRatio === 'number') {
        el.scrollTop = ctx.scrollRatio * (el.scrollHeight - el.clientHeight);
      }
      applyingRemoteNav.current = false;
    }, 120);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search, navigate]);
  applyRemoteNavRef.current = applyRemoteNav;

  // ── Host broadcasts navigation on route change (source of truth) ──
  useEffect(() => {
    if (!session || !isHost || applyingRemoteNav.current) return;
    const el = document.querySelector(COLLAB_SCROLL);
    const scrollRatio = el && el.scrollHeight > el.clientHeight ? el.scrollTop / (el.scrollHeight - el.clientHeight) : 0;
    cobrowseNav(session.sessionId, { route: location.pathname + location.search, scrollRatio });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search, session?.sessionId, isHost]);

  // ── Host broadcasts scroll (throttled) ───────────────────────────
  useEffect(() => {
    if (!session || !isHost) return;
    const el = document.querySelector(COLLAB_SCROLL);
    if (!el) return;
    let last = 0;
    const onScroll = () => {
      const now = Date.now();
      if (now - last < 140) return; last = now;
      const ratio = el.scrollHeight > el.clientHeight ? el.scrollTop / (el.scrollHeight - el.clientHeight) : 0;
      cobrowseNav(session.sessionId, { route: location.pathname + location.search, scrollRatio: ratio });
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.sessionId, isHost, location.pathname]);

  // ── Host re-broadcasts its current view periodically so any guest that
  // fell out of sync (late join, missed message) is pulled back. ──
  useEffect(() => {
    if (!session || !isHost) return;
    const id = setInterval(() => {
      const el = document.querySelector(COLLAB_SCROLL);
      const ratio = el && el.scrollHeight > el.clientHeight ? el.scrollTop / (el.scrollHeight - el.clientHeight) : 0;
      cobrowseNav(session.sessionId, { route: window.location.pathname + window.location.search, scrollRatio: ratio });
    }, 3000);
    return () => clearInterval(id);
  }, [session?.sessionId, isHost]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── HOST always streams its screen (rrweb); it is the single source of
  // truth even when a guest holds control (that guest's input is forwarded). ──
  useEffect(() => {
    if (!session || !isHost) {
      if (recordStopRef.current) { try { recordStopRef.current(); } catch { /* ignore */ } recordStopRef.current = null; }
      return;
    }
    const sid = session.sessionId;
    let stop = null;
    try {
      stop = record({
        emit(event) { cobrowseRR(sid, event); },
        recordCanvas: true,
        dataURLOptions: { type: 'image/jpeg', quality: 0.6 },
        sampling: { canvas: 4, mousemove: 40, scroll: 100, input: 'last' },
        collectFonts: true,
        inlineStylesheet: true,
        inlineImages: true,
        // don't record our own collab chrome (presence bar / cursors)
        blockClass: 'collab-norecord',
        blockSelector: '[data-collab-norecord]',
      });
    } catch { /* ignore */ }
    recordStopRef.current = stop || null;
    return () => { if (recordStopRef.current) { try { recordStopRef.current(); } catch { /* ignore */ } recordStopRef.current = null; } };
  }, [session?.sessionId, isHost]);

  const subscribeMirror = useCallback((cb) => {
    mirrorListenersRef.current.add(cb);
    return () => mirrorListenersRef.current.delete(cb);
  }, []);
  const requestMirror = useCallback(() => {
    const s = sessionRef.current; if (s) cobrowseRRRequest(s.sessionId);
  }, []);

  // ── Everyone broadcasts their own cursor (throttled) ──────────────
  useEffect(() => {
    if (!session) return;
    let last = 0;
    const onMove = (e) => {
      const now = Date.now();
      if (now - last < 45) return; last = now;
      cobrowseCursor(session.sessionId, e.clientX / window.innerWidth, e.clientY / window.innerHeight);
    };
    window.addEventListener('mousemove', onMove, { passive: true });
    return () => window.removeEventListener('mousemove', onMove);
  }, [session?.sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Auto-join from a shared ?collab= link, or restore across a refresh ──
  useEffect(() => {
    if (!user) return;
    const params = new URLSearchParams(window.location.search);
    const sid = params.get('collab');
    if (sid && !sessionRef.current) {
      // strip the param so refreshes don't re-trigger
      params.delete('collab');
      const clean = window.location.pathname + (params.toString() ? `?${params}` : '');
      window.history.replaceState({}, '', clean);
      joinSession(sid);
      return;
    }
    // Resilience: rehydrate a session that was active before an accidental refresh.
    if (!sessionRef.current) {
      try {
        const saved = JSON.parse(sessionStorage.getItem('collabSession') || 'null');
        if (saved && saved.sid && Date.now() - saved.ts < 60000) {
          connectMessaging();
          if (saved.isHost) {
            setSession({ sessionId: saved.sid, hostId: user.user_id, controllerId: user.user_id, members: [], isHost: true });
            cobrowseResume(saved.sid);
          } else {
            setSession({ sessionId: saved.sid, hostId: saved.hostId || null, controllerId: null, members: [], isHost: false });
            cobrowseJoin(saved.sid);
          }
        }
      } catch { /* ignore */ }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Persist the active session so a refresh can rehydrate it (grace-windowed).
  // Only WRITE here; clearing happens on explicit end / ended so an accidental
  // reload (session momentarily null on mount) never wipes the saved session.
  useEffect(() => {
    try {
      if (session) sessionStorage.setItem('collabSession', JSON.stringify({ sid: session.sessionId, isHost: session.isHost, hostId: session.hostId, ts: Date.now() }));
    } catch { /* ignore */ }
  }, [session?.sessionId, session?.isHost, session?.hostId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Actions ───────────────────────────────────────────────────────
  const startSession = useCallback(() => {
    if (!user) return;
    const sid = `cb_${user.user_id}_${Math.random().toString(36).slice(2, 8)}`;
    setSession({ sessionId: sid, hostId: user.user_id, controllerId: user.user_id, members: [], isHost: true });
    setCursors({});
    connectMessaging();
    cobrowseStart(sid);
    toast.success('Live session started', { description: 'Invite teammates to share your screen.' });
  }, [user]);

  const joinSession = useCallback((sid) => {
    if (!user || !sid) return;
    setSession({ sessionId: sid, hostId: null, controllerId: null, members: [], isHost: false });
    setCursors({}); setInvite(null);
    connectMessaging();
    cobrowseJoin(sid);
  }, [user]);

  const acceptInvite = useCallback(() => { if (invite) joinSession(invite.sessionId); }, [invite, joinSession]);
  const dismissInvite = useCallback(() => setInvite(null), []);

  const endSession = useCallback(() => {
    const s = sessionRef.current; if (!s) return;
    if (s.isHost) cobrowseEnd(s.sessionId); else cobrowseLeave(s.sessionId);
    setSession(null); setCursors({});
    try { sessionStorage.removeItem('collabSession'); } catch { /* ignore */ }
  }, []);

  // ── Control hand-over (host-driven; one controller at a time) ──────
  const grantControl = useCallback((userId) => {
    const s = sessionRef.current; if (!s || !s.isHost) return;
    cobrowseSetController(s.sessionId, userId);
  }, []);
  const revokeControl = useCallback(() => {
    const s = sessionRef.current; if (!s || !s.isHost) return;
    cobrowseSetController(s.sessionId, s.hostId);
  }, []);
  const sendInput = useCallback((input) => {
    const s = sessionRef.current; if (!s) return;
    cobrowseInput(s.sessionId, input);
  }, []);

  const inviteUsers = useCallback((userIds) => {
    const s = sessionRef.current; if (!s || !s.isHost || !userIds?.length) return;
    cobrowseInvite(s.sessionId, userIds.slice(0, 5));
  }, []);

  const joinLink = session ? `${window.location.origin}${location.pathname}?collab=${session.sessionId}` : '';
  const amController = !!(session && user && session.controllerId === user.user_id);
  const controlledByGuest = !!(session && session.controllerId && session.hostId && session.controllerId !== session.hostId);

  const value = {
    session, cursors, invite,
    isHost, isController, amController, controlledByGuest, currentUserId: user?.user_id,
    startSession, endSession, inviteUsers, acceptInvite, dismissInvite, joinSession, joinLink,
    subscribeMirror, requestMirror, grantControl, revokeControl, sendInput,
  };
  return <CollabContext.Provider value={value}>{children}</CollabContext.Provider>;
}
